import type { RestaurantDemoState } from "./restaurant-sandbox";
import type { RestaurantDemoV5State } from "./restaurant-demo-v5-state";

export interface RestaurantCustomerAgentHistoryMessage {
  readonly role: "customer" | "assistant";
  readonly text: string;
}

export interface RestaurantCustomerAgentAction {
  readonly capability: string;
  readonly label: string;
  readonly detail: string;
  readonly status: "succeeded" | "failed";
}

export interface RestaurantCustomerAgentResult {
  readonly workingState?: Readonly<Record<string, unknown>>;
  readonly response: string;
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly actions: readonly RestaurantCustomerAgentAction[];
  readonly handoffRequested: boolean;
  readonly runtime: {
    readonly mode: "real" | "fallback";
    readonly providerKey: string | null;
    readonly model: string | null;
    readonly rounds: number;
    readonly steps: number;
    readonly terminalReason: string;
  };
}

export class RestaurantCustomerAgentClientError extends Error {
  constructor(readonly code: string, message: string, readonly status?: number) {
    super(message);
    this.name = "RestaurantCustomerAgentClientError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validRequestId(value: string): boolean {
  return /^[A-Za-z0-9_-]{8,128}$/.test(value);
}

export async function runRestaurantCustomerAgent(input: {
  readonly requestId: string;
  readonly message: string;
  readonly history: readonly RestaurantCustomerAgentHistoryMessage[];
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly conversationId?: string;
  readonly workingState?: Readonly<Record<string, unknown>> | null;
}): Promise<RestaurantCustomerAgentResult> {
  const clientRequestId = input.requestId.trim();
  if (!validRequestId(clientRequestId)) throw new RestaurantCustomerAgentClientError("invalid_request_id", "El identificador del turno del Customer Agent es inválido.");
  const response = await fetch("/api/v1/platform/sales-demo/restaurant-customer-agent/turn", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ clientRequestId, conversationId: input.conversationId, workingState: input.workingState, message: input.message, history: input.history, state: input.state, v5: input.v5 }),
  });
  const raw = await response.text();
  let payload: unknown;
  try { payload = JSON.parse(raw) as unknown; }
  catch { throw new RestaurantCustomerAgentClientError("invalid_response", `Customer Agent devolvió HTTP ${response.status} sin JSON válido.`, response.status); }
  if (!isRecord(payload)) throw new RestaurantCustomerAgentClientError("invalid_response", "Customer Agent devolvió una respuesta inválida.", response.status);
  if (!response.ok) {
    const error = isRecord(payload.error) ? payload.error : {};
    throw new RestaurantCustomerAgentClientError(typeof error.code === "string" ? error.code : `http_${response.status}`, typeof error.message === "string" ? error.message : "Customer Agent no está disponible.", response.status);
  }
  if (!isRecord(payload.data)) throw new RestaurantCustomerAgentClientError("invalid_response", "Customer Agent no devolvió data.", response.status);
  return payload.data as unknown as RestaurantCustomerAgentResult;
}
